<script lang="ts">
    import { redirect } from "@roxi/routify";
    import { UserService } from "../services/UserService";
    import { BackgroundService } from "../services/BackgroundService";
    import { clickAction } from "$/utils/clickAction";
    import { getEffectivePricingPlan } from "$/utils/Utils";
    import {
        ActiveTopicStore,
        ActiveTopic,
        TopicAccessLevel,
    } from "$/stores/extension/ActiveTopicStore";
    import { TenantStore } from "$/stores/TenantStore";
    import { tooltip } from "$/utils/tooltip";
    import CollaboratorSuggestions from "./CollaboratorSuggestions.svelte";

    let UserServiceImpl = chrome?.storage ? BackgroundService : UserService;

    export let owner;
    export let addRole = async (role) => {};
    export let existingRoles;

    let role: number = TopicAccessLevel.VIEWER;
    let username: string = "";
    let usernameExists: boolean = false;
    let notValidEmail: boolean = true;
    let waitingUsernameValidation: boolean = false;
    let errorMessage: string = "";
    let timer: ReturnType<typeof setTimeout>;
    let isFocused = false;
    let hasSuggestions = false;

    $: shouldCheckFreeTierLimits =
        getEffectivePricingPlan($ActiveTopic?.tenant) < 1 &&
        $TenantStore?.collaborators.length >= 5;
    $: notValidEmail,
        waitingUsernameValidation,
        (errorMessage = getErrorMessage(username));
    $: showErrorMessage = errorMessage !== "" && !waitingUsernameValidation;
    $: submitAllowed =
        !showErrorMessage && username !== "" && !waitingUsernameValidation;
    $: showUpgradeButton = errorMessage === "Free tier limit reached.";
    $: {
        if (username === "") {
            usernameExists = false;
            notValidEmail = true;
        } else {
            debounceCheckUsernameExists();
        }
    }
    function getErrorMessage(inputValue: string) {
        if (inputValue === "" || hasSuggestions) {
            return "";
        }
        if (notValidEmail) {
            return "Invalid email address.";
        }
        if (inputValue === owner.username) {
            return "Replacing owner is not allowed.";
        }
        if (
            existingRoles &&
            existingRoles.some(
                (existingRole) =>
                    existingRole.user.username === inputValue &&
                    existingRole.role === role,
            )
        ) {
            return "This user is already a member with the same role.";
        }
        if (shouldCheckFreeTierLimits && !isCollaboratorOrMember(inputValue)) {
            return "Free tier limit reached.";
        }
        return "";
    }

    function isCollaboratorOrMember(newUsername) {
        return (
            $TenantStore?.collaborators.some(
                (collaborator) => collaborator.username === newUsername,
            ) ||
            existingRoles.some(
                (existingRole) => existingRole.user.username === newUsername,
            )
        );
    }

    /**
     * Waits for user to stop typing for 500ms before checking if the username exists.
     */
    function debounceCheckUsernameExists() {
        clearTimeout(timer);

        if (
            username === owner.username ||
            username === "" ||
            isCollaboratorOrMember(username)
        ) {
            usernameExists = true;
            notValidEmail = false;
            waitingUsernameValidation = false;
            return;
        }

        waitingUsernameValidation = true;

        timer = setTimeout(async () => {
            // check if username is valid email address
            if (
                username.match(
                    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/,
                )
            ) {
                // check if username exists
                usernameExists = await UserServiceImpl.usernameExists(username);
                notValidEmail = false;

                // not a valid email address
            } else {
                usernameExists = false;
                notValidEmail = true;
            }

            waitingUsernameValidation = false;
        }, 500);
    }

    function handleRoleAdded() {
        let topicRole = {
            role,
            user: {
                username,
            },
        };

        addRole(topicRole);

        username = "";
    }

    const handleSuggestionSelected = (event) => {
        username = event.detail;
    };

    function handleKeyDown(event) {
        if(hasSuggestions) { 
            return;
        }
        if ( username!="") { 
            if (event.key === "Enter" && submitAllowed) {
                handleRoleAdded();
            }
        }
    }
    const updateHasSuggestions = (visible) => {
        hasSuggestions = visible;
    }
  
</script>

<div class="add-topic-role">
  
    {#if isFocused && username!=""}
        <div class="collaborator-suggestions-wrapper">
            <CollaboratorSuggestions query={username} on:select={handleSuggestionSelected}   updateHasSuggestions={updateHasSuggestions} />
        </div>
    {/if}
    <div
        class="main-content"
        class:main-content--warning={showErrorMessage}
        class:main-content--free-limit-reached={showUpgradeButton}
    >
        <input
            class="email-input"
            placeholder="Email address"
            name="email"
            type="email"
            autocomplete="off"
            bind:value={username}
            on:keydown={handleKeyDown}
            tabindex="0"
            on:focus={() => isFocused = true}  
            on:blur={() => isFocused = false}  
        />

        {#if waitingUsernameValidation}
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-5 animate-spin"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                />
            </svg>
        {:else if submitAllowed && !showUpgradeButton}
            <div
                class="public-role-settings__icon"
                use:clickAction={handleRoleAdded}
            >
                {#if usernameExists}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="h-6 w-6"
                        use:tooltip={"Share with user"}
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z"
                        />
                    </svg>
                {:else}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="h-6 w-6"
                        use:tooltip={"Invite user via email"}
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                        />
                    </svg>
                {/if}
            </div>
        {/if}

        {#if showUpgradeButton}
            <button
                class="upgrade-button"
                on:click={() => $redirect("/payment")}
            >
                Upgrade to <b class="upgrade-button__bold">Pro</b>
            </button>
        {:else}
            <select
                class="public-role-settings__public-select"
                bind:value={role}
            >
                <option value={TopicAccessLevel.VIEWER}>Viewer</option>
                <option value={TopicAccessLevel.COMMENTER}>Commenter</option>
                <option value={TopicAccessLevel.EDITOR}>Editor</option>
            </select>
        {/if}
    </div>

    {#if showErrorMessage}
        <span class="email-message">
            {errorMessage}
        </span>
    {/if}
</div>

<style>
    @import "../main.css";

    .collaborator-suggestions-wrapper {
        @apply absolute bottom-12 right-0 z-50 w-full !important;
    }

    .add-topic-role {
        @apply flex flex-col gap-1 relative !important;
    }

    .email-input {
        @apply reset w-full bg-transparent text-sm !important;
    }

    .email-message {
        @apply mx-6 text-xs text-pink-500 !important;
    }

    .email-message b {
        @apply reset text-xs font-bold text-pink-500 !important;
    }

    .main-content {
        @apply flex flex-row items-center gap-1 rounded-full border border-gray-200 bg-white p-4 !important;
    }

    .main-content.main-content--free-limit-reached {
        @apply py-2.5 pr-2 !important;
    }

    .main-content--warning {
        @apply border-pink-500 !important;
    }

    .public-role-settings__public-select {
        @apply reset ml-auto min-w-fit appearance-auto bg-white text-right text-xs outline-none !important;
    }

    .public-role-settings__icon {
        @apply flex h-4 min-h-fit w-4 min-w-fit cursor-pointer items-center overflow-visible !important;
    }

    .public-role-settings__icon svg {
        @apply reset h-5 w-5 cursor-pointer !important;
    }

    .upgrade-button {
        @apply reset ml-auto h-8 shrink-0 rounded-full bg-yellow-300 px-4 py-1 text-xs tracking-tight text-black transition-all hover:scale-105 hover:bg-yellow-400 !important;
    }

    .upgrade-button__bold {
        @apply reset text-xs font-bold tracking-tight text-inherit !important;
    }
</style>
